/**
 * FundSummary.js — the whole book, at the top of the list
 * =======================================================
 * Usability review of 23 October 2026, item 25: *"Make the totals card at the
 * top clearer. "Fund max cash exposure −$10.5M (Nov 4, 25)" needs a one-line
 * plain-English label. The "55 / 59 complete" count should be a progress
 * indicator, with the 4 incomplete deals one click away."*
 *
 * Every figure here is over every deal with cash movement, whatever its
 * status, and the card says so in one line: the filter and the search below
 * change the list, never these, and two counts a few inches apart that
 * disagree read as a defect unless the page says which population each is
 * over (CLAUDE.md 0.17). The definitions sit behind the ⓘ.
 *
 * And the fund total is not a total while the book mixes currencies with a
 * rate missing: when the API reports `fund_total_is_currency_safe: false` the
 * card names the deals left out rather than implying a currency.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "../../components/ui";
import { InfoTip } from "../../components/PageTools";
import { dealReportPath } from "../../components/links";
import theme from "../../components/theme";
import { currencyOf, day, dealMoney, shares } from "./figures";
import { Tile } from "./Tiles";

/** Why a deal's cost basis is incomplete, in the words a reader can act on. */
export function basisReasons(deal) {
  const c = deal?.completeness || {};
  const bits = [];
  if (Number(c.uncovered_shares_sold) > 0) {
    bits.push(
      `${shares(c.uncovered_shares_sold)} shares were sold with no cost behind them` +
        (Number(c.proceeds_without_basis) > 0
          ? `, for ${dealMoney(c.proceeds_without_basis, currencyOf(deal))} of proceeds`
          : "")
    );
  }
  if (c.notices_awaiting_review > 0) {
    bits.push(
      `${c.notices_awaiting_review} notice${c.notices_awaiting_review === 1 ? " is" : "s are"} still awaiting review`
    );
  }
  if (c.unposted_broker_rows > 0) {
    bits.push(
      `${c.unposted_broker_rows} broker ${c.unposted_broker_rows === 1 ? "row is" : "rows are"} imported but not posted`
    );
  }
  return bits;
}

function Completeness({ total, incomplete }) {
  const [open, setOpen] = useState(false);
  const complete = total - incomplete.length;
  const share = total ? (complete / total) * 100 : 0;
  return (
    <div className="dr-tile dr-progress-tile">
      <div className="dr-tile-label">
        Cost basis
        <InfoTip align="right">
          A deal whose sales outrun its loaded cost reports a return it has not made: its uncosted
          disposals book their whole proceeds as gain. Those deals are marked provisional until the
          missing cost, notice or broker row is in.
        </InfoTip>
      </div>
      <div className="dr-tile-value" style={{ fontSize: 16 }}>
        {complete} of {total} deals have full cost basis
      </div>
      <div
        className="dr-progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={complete}
        aria-label={`${complete} of ${total} deals have full cost basis`}
      >
        <span style={{ width: `${share}%` }} />
      </div>
      {incomplete.length > 0 ? (
        <button type="button" className="pt-link no-print" aria-expanded={open} onClick={() => setOpen((o) => !o)} style={{ fontSize: 12, marginTop: 6 }}>
          {open ? "Hide" : "Show"} the {incomplete.length} incomplete {incomplete.length === 1 ? "deal" : "deals"}
        </button>
      ) : (
        <div className="dr-tile-note">Every deal's cost is loaded.</div>
      )}
      {open && (
        <ul className="dr-incomplete">
          {incomplete.map(({ name, deal }) => {
            const reasons = deal ? basisReasons(deal) : [];
            return (
              <li key={name}>
                {deal ? <Link to={dealReportPath(deal.deal.id)} className="dr-link">{name}</Link> : name}
                {reasons.length > 0 && <span style={{ color: theme.textMuted }}> — {reasons.join("; ")}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function FundSummary({ data, deals }) {
  const fund = data.fund || {};
  const assumed = data.deals_with_assumed_currency || [];
  const byName = new Map(deals.map((d) => [d.deal.name, d]));
  const incomplete = (data.deals_with_incomplete_basis || []).map((row) => ({ name: row.deal, deal: byName.get(row.deal) }));
  const total = data.deal_count ?? deals.length;
  return (
    <Card>
      <div className="dr-fund">
        <Tile
          label="Most cash tied up at once"
          value={dealMoney(fund.max_cash_exposure, "USD")}
          tone="negative"
          note={fund.max_cash_exposure_date ? `on ${day(fund.max_cash_exposure_date)}` : "no cash was deployed"}
          info={
            <>
              <p>
                The deepest point of the fund's cumulative cash curve: the most that every deal
                together had out at one time, net of what had already come back.
              </p>
              <p>
                It is not the sum of each deal's own deepest point — two deals that each tied up a
                million, one repaid before the other was funded, never tied up two million between
                them.
              </p>
            </>
          }
        />
        <Tile
          label="Net cash to date"
          value={dealMoney(fund.net_cash, "USD")}
          tone={Number(fund.net_cash) >= 0 ? "positive" : "negative"}
          note="cash back less cash out, every deal"
          info="Everything that has come back less everything that went out, in dollars at each movement's own fixing. It leaves out what is still held, so it is cash, not profit."
        />
        <Completeness total={total} incomplete={incomplete} />
      </div>
      {!data.fund_total_is_currency_safe && (
        <div className="dr-warning" style={{ marginTop: 12 }}>
          <strong style={{ color: theme.text }}>Deals missing from these figures.</strong>{" "}
          {(data.deals_not_in_fund_total || []).map((d) => d.deal).join(", ")} — an exchange rate is
          missing for one of their dates, so they are left out rather than added at face value.
        </div>
      )}
      {assumed.length > 0 && (
        <div className="dr-warning" style={{ marginTop: 12 }}>
          <strong style={{ color: theme.text }}>Currency assumed.</strong>{" "}
          {assumed.map((d) => d.deal).join(", ")} also trade in{" "}
          {[...new Set(assumed.flatMap((d) => d.alongside || []))].join("/")}, and the ledger records
          no currency on some of their cash, which is read as dollars.
        </div>
      )}
      <div className="dr-fund-scope">
        All {total} deals with cash movement, whatever their status — the filter and the search below
        change the list, not these figures.
      </div>
    </Card>
  );
}
